import { ErrorAlert } from "../../components/alerts";
import { Project } from "@/api/project";
import { ProjectSchema } from "../ProjectSchema";
import { SimilarProjects } from "../components/SimilarProjects";
import { SpinButton } from "@/components/form/SpinButton";
import { useForm } from "react-hook-form";
import { useModal } from "@/hooks";
import { useProjectValidation, useSaveProject, useSimilarProjects } from "../hooks";
import { v4 as uuid } from "uuid";
import { zodResolver } from "@hookform/resolvers/zod";

import styles from "./NewProject.tsx.module.css";

export type NewProjectDialogProps = {
  onAccept: (project: Project) => void;
};

export function NewProjectDialog(props: NewProjectDialogProps) {
  const modal = useModal();
  const save = useSaveProject();

  const form = useForm<Project>({
    defaultValues: { id: uuid() },
    resolver: zodResolver(ProjectSchema),
    reValidateMode: "onChange",
  });

  const formData = form.watch();
  const similar = useSimilarProjects(formData);
  const validate = useProjectValidation(form);
  const errors = form.formState.errors;

  function handleSelectSimilar(project: Project) {
    modal.close();
    props.onAccept(project);
  }

  async function handleAccept(project: Project) {
    if (await validate(formData)) {
      await save.mutateAsync(project);
      modal.close();

      if (props.onAccept) {
        props.onAccept(project);
      }
    }
  }

  return (
    <div className={styles.dialog}>
      <form className="px-2" autoComplete="off" onSubmit={form.handleSubmit(handleAccept)}>
        <h5 className={styles.header}>New Project</h5>
        <div className={styles.body}>
          <div className="form-group mb-2">
            <label htmlFor="name">
              Project Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              className={`form-control ${errors.name && "border-danger"}`}
              {...form.register("name")}
            />
            {errors.name && <span className="text-danger">{errors.name.message}</span>}
          </div>
          <div className="form-group mb-2">
            <label htmlFor="number">
              Project Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={50}
              className={`form-control ${errors.number && "border-danger"}`}
              {...form.register("number")}
            />
            {errors.number && <span className="text-danger">{errors.number.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="description">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              rows={5}
              cols={50}
              maxLength={100}
              className={`form-control ${errors.description && "border-danger"}`}
              {...form.register("description")}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>
        </div>

        {errors.root && <ErrorAlert>{errors.root?.message}</ErrorAlert>}

        {similar.data && similar.data.length > 0 && (
          <>
            <h5>Similar Projects</h5>
            <SimilarProjects project={formData} similar={similar.data} onSelect={handleSelectSimilar} />
          </>
        )}

        <div className={styles.footer}>
          <SpinButton type="submit" className="btn btn-default me-1" loading={save.isLoading} disabled={save.isLoading}>
            OK
          </SpinButton>
          <button type="button" className="btn btn-default" onClick={modal.close} disabled={save.isLoading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
